import { useEffect, useState } from 'react';
import { api, formatBytes, formatDuration, formatWib, type CameraHealth } from '../lib/api';
import { useBahasa } from '../i18n';

/**
 * Panel kesehatan satu kamera (§G10.2, §D9).
 *
 * Satu baris ringkas, bukan layar tersendiri: halaman ini milik rekamannya, dan
 * kesehatan kamera di sini berperan sebagai konteks. Rincian tersedia sekali
 * klik, tertutup secara bawaan — yang dibutuhkan sehari-hari cuma "sehat atau
 * tidak".
 */

/** §D9.2 — di-poll 30 detik. Yang ditanyakan "masih sehat?", bukan "berapa milidetik". */
const POLL_MS = 30_000;

type Tingkat = 'sehat' | 'waspada' | 'bermasalah';

/** Warna SELALU berpasangan dengan teks (§G3) — tidak ada arti yang hanya warna. */
const WARNA: Record<Tingkat, string> = {
  sehat: 'bg-green-600',
  waspada: 'bg-amber-500',
  bermasalah: 'bg-red-600',
};

/** Ambang §D9.2. 15 detik = 60 frame berturut-turut yang tidak datang pada 4 fps. */
function tingkatJeda(lagS: number | null): Tingkat {
  if (lagS === null) return 'bermasalah';
  if (lagS <= 15) return 'sehat';
  if (lagS <= 60) return 'waspada';
  return 'bermasalah';
}

function tingkatCakupan(ratio: number): Tingkat {
  if (ratio >= 0.99) return 'sehat';
  if (ratio >= 0.95) return 'waspada';
  return 'bermasalah';
}

function Angka({ label, nilai, tingkat }: { label: string; nilai: string; tingkat?: Tingkat }) {
  return (
    <span className="flex items-center gap-1.5">
      {tingkat && (
        <span className={`h-2.5 w-2.5 shrink-0 rounded-full ${WARNA[tingkat]}`} aria-hidden="true" />
      )}
      <span className="text-muted">{label}</span>
      <span className="font-medium tabular-nums">{nilai}</span>
    </span>
  );
}

export function CameraHealthBar({ cameraId }: { cameraId: string }) {
  const { t } = useBahasa();
  const [health, setHealth] = useState<CameraHealth | null>(null);
  const [gagal, setGagal] = useState(false);
  const [buka, setBuka] = useState(false);

  useEffect(() => {
    let batal = false;
    const muat = () =>
      api<CameraHealth>(`/cameras/${cameraId}/health`)
        .then((h) => {
          if (!batal) {
            setHealth(h);
            setGagal(false);
          }
        })
        .catch(() => !batal && setGagal(true));

    void muat();
    const timer = window.setInterval(() => void muat(), POLL_MS);
    return () => {
      batal = true;
      window.clearInterval(timer);
    };
  }, [cameraId]);

  if (gagal && !health) {
    return (
      <p className="mb-2 text-sm text-muted" role="status">
        {t('Camera status is unavailable.')}
      </p>
    );
  }
  if (!health) return <p className="mb-2 text-sm text-muted">{t('Checking camera status…')}</p>;

  const jeda = tingkatJeda(health.frame.lagS);
  const cakupan = tingkatCakupan(health.coverage.ratio);
  const statusTingkat: Tingkat =
    health.status === 'online' ? 'sehat' : health.status === 'error' ? 'bermasalah' : 'waspada';

  return (
    <div className="mb-2 rounded-lg border border-line bg-surface">
      <button
        type="button"
        onClick={() => setBuka((v) => !v)}
        aria-expanded={buka}
        className="flex min-h-tap w-full flex-wrap items-center gap-x-5 gap-y-1 px-3 py-2 text-left text-sm hover:bg-subtle"
      >
        <Angka
          label={t('Status')}
          tingkat={statusTingkat}
          nilai={
            health.status === 'online'
              ? health.isEnabled && health.recordEnabled
                ? t('REC')
                : t('ONLINE')
              : health.status === 'error'
                ? t('ERROR')
                : t('OFFLINE')
          }
        />
        {/* "Tidak ada data" bukan nol: kunci yang hilang di Redis berarti tidak
            ada frame belakangan ini (§D9.1), dan itu harus terbaca begitu. */}
        <Angka
          label={t('Frame lag')}
          tingkat={jeda}
          nilai={health.frame.lagS === null ? t('no data') : `${health.frame.lagS}s`}
        />
        <Angka
          label={t('Bitrate')}
          nilai={
            health.bitrate.mbps === null
              ? t('not measured yet')
              : `${health.bitrate.mbps.toFixed(2)} Mbps`
          }
        />
        {/* Kamera View Only (§D2) tidak punya rekaman, jadi cakupannya selalu
            0% — angka yang benar secara aritmetika dan terbaca sebagai kamera
            rusak. Yang ditampilkan modenya, bukan rasionya. */}
        {health.recordEnabled ? (
          <Angka
            label={
              health.coverage.fullWindow ? t('Coverage, last 24 h') : t('Coverage since start')
            }
            tingkat={cakupan}
            nilai={`${(health.coverage.ratio * 100).toFixed(1)}%`}
          />
        ) : (
          <Angka label={t('Recording')} nilai={t('View only')} />
        )}
        <span className="ml-auto text-xs text-muted">
          {buka ? t('Hide details') : t('Details')}
        </span>
      </button>

      {buka && (
        <dl className="grid gap-x-8 gap-y-1 border-t border-line px-3 py-2 text-sm sm:grid-cols-2 lg:grid-cols-3">
          <Baris
            label={t('Connected since')}
            nilai={health.connectedAt ? formatWib(health.connectedAt) : '—'}
          />
          <Baris
            label={t('Estimated storage')}
            nilai={
              health.bitrate.gbPerDay === null
                ? '—'
                : t('{n} GB per day', { n: health.bitrate.gbPerDay })
            }
          />
          <Baris
            label={t('Retention')}
            nilai={
              health.recordEnabled
                ? t('{n} days', { n: health.storage.maxDay })
                : t('not recording')
            }
          />
          <Baris
            label={t('Recordings in this window')}
            nilai={`${t('{n} segments', { n: health.storage.segments })} · ${formatBytes(
              health.storage.bytes,
            )}`}
          />
          <Baris
            label={t('Gaps')}
            nilai={
              !health.recordEnabled
                ? '—'
                  : health.coverage.gaps === 0
                    ? t('none')
                    : `${health.coverage.gaps} · last at ${formatWib(health.coverage.lastGap!.startedAt)} (${formatDuration(
                        health.coverage.lastGap!.durationS,
                      )})`
            }
          />
          {health.lastError && <Baris label={t('Last error')} nilai={health.lastError} />}

          <div className="sm:col-span-2 lg:col-span-3">
            <dt className="text-muted">{t('Recent events')}</dt>
            <dd className="mt-1">
              {health.events.length === 0 ? (
                <span className="text-muted">{t('No events recorded.')}</span>
              ) : (
                <ul className="space-y-0.5">
                  {health.events.map((e, i) => (
                    <li key={`${e.event}-${i}`} className="flex flex-wrap items-baseline gap-2">
                      <span
                        className={
                          e.level === 'error'
                            ? 'text-red-600'
                            : e.level === 'warn'
                              ? 'text-amber-600'
                              : 'text-muted'
                        }
                      >
                        {e.level.toUpperCase()}
                      </span>
                      <span>{e.message}</span>
                      {e.occurrenceCount > 1 && (
                        <span className="text-muted">×{e.occurrenceCount}</span>
                      )}
                      <span className="text-xs text-muted">{formatWib(e.lastOccurredAt)}</span>
                    </li>
                  ))}
                </ul>
              )}
            </dd>
          </div>
        </dl>
      )}
    </div>
  );
}

function Baris({ label, nilai }: { label: string; nilai: string }) {
  return (
    <div className="flex items-baseline gap-2">
      <dt className="text-muted">{label}</dt>
      <dd className="font-medium">{nilai}</dd>
    </div>
  );
}
